import { Button } from '@src/features/ui';
import StepProgress from '@src/features/user/checkout/layout/StepProgress';
import { useNavigate } from 'react-router-dom';
import { useCheckoutStore } from '@src/stores/checkoutStore';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AddonType, TradingPlatForms } from '@src/types';
import { AddonPerformanceAccountFee } from '@constants/addons';
import { fetchAddOnsData } from '@src/services/products';
import { useGetMe } from '@src/hooks/useGetMe';

import { TermAndCondition } from '../TermAndCondition';

import {
  accountTypeData,
  iconsImgPackage,
  iconsPackage,
} from './data';

const MONTHLY_EXCLUDED_ADDON_TYPES = new Set([
  'performance_account_fee',
  'eval_discount_x_reset',
]);

const SUBSCRIPTION_REBILL_TYPES = new Set([
  're_bill_second_price_until_cancelled',
  'charge_second_price_once',
  'charge_second_price_x_times',
]);

interface AddOnItem {
  id: number;
  name: string;
  price: number;
  description: string;
  addon_type: string;
  title: string;
  billing_plan: {
    first_price: number;
    first_period_unit: string;
    addon_monthly_price?: number;
    second_price?: number;
  };
  display_name_web: string;
  account_benefits?: string | null;
  category: string;
  addon_additional_drawdown_option: number | null;
  addon_performance_account_fee_option?: any;
  addon_discount_percent?: number | null;
  priceLater?: number;
}

interface AddOnDataItem {
  uid: string;                 // stable identifier: "addonType__s_idx" or "api_ID"
  addonId?: number;            // real backend addon ID (only for API items)
  price?: number;
  title: string;
  sub?: string;
  first_period_unit?: string;
  addon_performance_account_fee_option?: AddonPerformanceAccountFee;
  priceNote?: string;
  discount_percent?: number | null;
  priceLater?: number;
  rangular?: number;
  addonMonthlyPrice?: number;
}

interface MappedAddOnData {
  title: string;
  title2?: string;
  description: string;
  optional?: boolean;
  note?: string;
  addon_type: string;
  data: AddOnDataItem[];
  data2?: AddOnDataItem[];
}

const titles: Record<
  string,
  {
    title: string;
    note?: string;
    desc?: string;
  }
> = {
  eval_quality_x_days: {
    title: 'Min days to qualify after reaching profit target ',
    note: '<b>Note</b>: Payout requests may be submitted on the nearest Saturday between <b>2:00 AM and 5:00 PM EST.</b>',
    desc: 'This is a one-time charge',
  },
  eval_discount_x_reset: {
    title: 'Practice Resets',
    note: '<b>Note</b>: A reset does not change the subscription renewal date (monthly plan) or the <b>30-day expiry</b> (non-recurring plan).',
    desc: 'Save by buying now',
  },
  pa_payout_x_days: {
    title: 'Payout Speed',
    note: '<b>Note</b>: Payout requests may be submitted on the nearest Saturday between <b>2:00 AM and 5:00 PM EST.</b>',
    desc: 'This is a one-time charge',
  },
  performance_account_fee: { title: 'Performance Account Fee' },
  account_bundle: { title: 'Want to make it into a bundle?' },
  drawdown: { title: 'Drawdown' },
  // pa_change_30pct_rule: { title: '30% Rule' },
};

const availableAddOnsTypes: Record<number, string[]> = {
  1: [
    'default',
    'eval_quality_x_days',
    'eval_discount_x_reset',
    'pa_payout_x_days',
    'performance_account_fee',
    'account_bundle',
    'drawdown',
    'pa_change_30pct_rule',
  ],
  2: ['pa_payout_x_days', 'drawdown', 'pa_change_30pct_rule'],
  3: [
    'default',
    'eval_quality_x_days',
    'eval_discount_x_reset',
    'pa_payout_x_days',
    'performance_account_fee',
    'account_bundle',
    'drawdown',
    'pa_change_30pct_rule',
  ],
};

export const AddOns = () => {
  const navigate = useNavigate();
  const {
    platform,
    packages,
    addOns,
    sizeIndex,
    typeIndex,
    setPayment,
    setAddOns,
    isPurchaseMode,
    setPlatform,
    isCompetitionAccount,
  } = useCheckoutStore();

  const { me } = useGetMe();
  const [showTerm, setShowTerm] = useState(false);

  const [addOnsData, setAddOnsData] = useState<MappedAddOnData[]>(
    []
  );

  useEffect(() => {
    // If in purchase mode and platform is not set, get it from me.platform
    if (isPurchaseMode && !platform && me?.platform) {
      setPlatform(me.platform);
    }
  }, [isPurchaseMode, platform, me, setPlatform]);

  const getAddOnsData = useCallback(async () => {
    try {
      const { data } = await fetchAddOnsData(platform);

      const mappedData = (data as AddOnItem[]).reduce<MappedAddOnData[]>(
        (prev, item) => {
          if (
            [
              'default',
              'eval_quality_x_days',
              'eval_discount_x_reset',
              'pa_payout_x_days',
              'performance_account_fee',
              'account_bundle',
              'drawdown',
              'pa_change_30pct_rule',
            ].includes(item.addon_type)
          ) {
            const existing = prev.find((e) => e.addon_type === item.addon_type);

            if (existing) {
              if (item.addon_additional_drawdown_option) {
                existing.addon_type = item.addon_type;
                existing.data2 = [...(existing?.data2 || [])].filter(Boolean);
                existing.title2 =
                  item.addon_type === AddonType.Drawdown
                    ? 'Add to your current drawdown additional drawdown (optional; cumulative; max +$1,500)'
                    : existing.title2;
                existing.data2.push({
                  uid: `api2_${item.id}`,
                  addonId: item.id,
                  price: item.billing_plan?.first_price,
                  addonMonthlyPrice: item.billing_plan?.addon_monthly_price,
                  title: item.addon_additional_drawdown_option.toString() || '',
                  sub: item.description,
                  first_period_unit: item.billing_plan?.first_period_unit,
                });
                existing.data2.sort(
                  (a, b) => Number(a.title) - Number(b.title)
                );

                return prev;
              } else {
                existing.data.push({
                  uid: `api_${item.id}`,
                  addonId: item.id,
                  price: item.billing_plan?.first_price,
                  priceLater: item.billing_plan?.second_price,
                  addonMonthlyPrice: item.billing_plan?.addon_monthly_price,
                  title: item.display_name_web,
                  sub: item.description,
                  first_period_unit: item.billing_plan?.first_period_unit,
                  priceNote: item?.account_benefits || '',
                  addon_performance_account_fee_option:
                    item.addon_performance_account_fee_option,
                  discount_percent: item.addon_discount_percent,
                });
              }
            } else {
              const addonType = item.addon_type;
              prev.push({
                addon_type: addonType,
                note: titles[addonType].note || '',
                optional: addonType === 'eval_discount_x_reset',
                title: titles[addonType].title || '',
                title2:
                  addonType === AddonType.Drawdown &&
                  !!item.addon_additional_drawdown_option
                    ? 'Add to your current drawdown additional drawdown (optional; cumulative; max +$1,500)'
                    : undefined,
                data: !item.addon_additional_drawdown_option
                  ? [
                      {
                        uid: `api_${item.id}`,
                        addonId: item.id,
                        price: item.billing_plan?.first_price,
                        addonMonthlyPrice: item.billing_plan?.addon_monthly_price,
                        title: item.display_name_web,
                        sub: item.description,
                        first_period_unit: item.billing_plan?.first_period_unit,
                        addon_performance_account_fee_option:
                          item.addon_performance_account_fee_option,
                        priceNote: item?.account_benefits || '',
                      },
                    ]
                  : [],
                data2: item.addon_additional_drawdown_option
                  ? [
                      {
                        uid: `api2_${item.id}`,
                        addonId: item.id,
                        title:
                          item.addon_additional_drawdown_option.toString() ||
                          '',
                        price: item.billing_plan?.first_price,
                        addonMonthlyPrice: item.billing_plan?.addon_monthly_price,
                        sub: item.description,
                        first_period_unit: item.billing_plan?.first_period_unit,
                      },
                    ]
                  : undefined,
                description: item.description,
              });
            }

            return prev;
          }

          return prev;
        },
        [
          {
            optional: false,
            title: 'Payment & Activation Handling',
            addon_type: 'default',
            data: [
              {
                uid: 'default__s_0',
                title: 'Monthly subscription',
                sub: 'Renews every 30 calendar days ',
                priceNote: 'Auto-reset at renewal',
                price: 5,
                first_period_unit: 'month',
              },
              {
                uid: 'default__s_1',
                title: 'Non-recurring (no subscription)',
                sub: 'Single charge; expires after 30 calendar days',
                priceNote: 'No auto-reset',
                price: 0,
              },
            ],
          },
          {
            optional: false,
            title: 'Want to make it into a bundle?',
            addon_type: 'account_bundle',
            data: [
              {
                uid: 'account_bundle__s_0',
                title: 'No, thanks! Keep only 1 account',
                priceNote: 'Auto-reset at renewal',
                price: 0,
              },
            ],
          },
          {
            optional: false,
            title: 'Drawdown',
            addon_type: 'drawdown',
            data: [
              {
                uid: 'drawdown__s_0',
                title: 'Trailing Drawdown',
                price: 0,
              },
            ],
            data2: [
              {
                uid: 'drawdown__s2_0',
                title: '$0 part of base price',
                price: 0,
              },
            ],
          },
          {
            optional: false,
            title: 'Payout Speed',
            addon_type: 'pa_payout_x_days',
            data: [
              {
                uid: 'pa_payout_x_days__s_0',
                title: 'Last Saturday monthly —(No price change)',
                price: 0,
              },
            ],
          },
          {
            optional: false,
            title: 'Performance Account Fee',
            addon_type: 'performance_account_fee',
            data: [],
          },
          {
            optional: true,
            title: 'Practice Resets',
            addon_type: 'eval_discount_x_reset',
            data: [
              {
                uid: 'eval_discount_x_reset__s_0',
                title: 'No Thanks',
                price: 0,
                sub: 'I will pay full price later $85',
              },
            ],
          },
          {
            optional: false,
            title: 'Min days to qualify after reaching profit target ',
            addon_type: 'eval_quality_x_days',
            data: [
              {
                uid: 'eval_quality_x_days__s_0',
                title: 'Standard 10 Days (No price change)',
                sub: '',
                price: 0,
              },
            ],
          },
          // {
          //   optional: false,
          //   title: '30% Rule',
          //   addon_type: 'pa_change_30pct_rule',
          //   data: [
          //     {
          //       title: 'No, thanks! Keep it standard',
          //       sub: '',
          //       price: 0,
          //     },
          //   ],
          // },
        ] as MappedAddOnData[]
      );

      setAddOnsData([...mappedData]);
    } catch (error) {
      console.error('Error fetching add-ons data:', error);
    }
  }, [platform]);

  useEffect(() => {
    if (!platform) navigate('/select-trading-platform');
    getAddOnsData().then();
  }, [navigate, platform]);

  const goBack = () => {
    navigate('/select-account');
  };

  /** Find an item in a group by its uid (searches both data and data2). */
  const findItemByUid = (group: MappedAddOnData, uid: string): AddOnDataItem | undefined =>
    group.data.find((d) => d.uid === uid) ?? group.data2?.find((d) => d.uid === uid);

  /** Returns true if a given uid is currently selected within its group. */
  const isItemSelected = (addonType: string, uid: string): boolean => {
    const sel = addOns[addonType];
    if (!sel) return false;
    if (Array.isArray(sel)) return sel.includes(uid);
    return sel === uid;
  };

  /**
   * Select/toggle an addon item by uid.
   * Drawdown: multi-select (one from data, optional multi from data2).
   * All others: single-select per group.
   */
  const selectAddOns = (addonType: string, uid: string) => {
    const group = addOnsData.find((g) => g.addon_type === addonType);
    if (!group) return;

    const isDrawdownMulti = addonType === 'drawdown' && !!group.data2?.length;

    if (isDrawdownMulti) {
      const current = (
        Array.isArray(addOns[addonType]) ? addOns[addonType] : []
      ) as string[];
      const isData2Item = !!group.data2?.some((d) => d.uid === uid);
      const dataSelections = current.filter((u) => group.data.some((d) => d.uid === u));
      const data2Selections = current.filter((u) => group.data2!.some((d) => d.uid === u));

      if (isData2Item) {
        // data2 is single-select: toggle off if already selected, otherwise replace
        const newData2 = data2Selections.includes(uid) ? [] : [uid];
        setAddOns({
          ...addOns,
          [addonType]: newData2.length > 0 ? [...dataSelections, ...newData2] : dataSelections,
        });
      } else {
        // Switch data item while keeping current data2 selection
        setAddOns({ ...addOns, [addonType]: [uid, ...data2Selections] });
      }
    } else {
      setAddOns({ ...addOns, [addonType]: uid });
    }
  };

  /** Compute the price charged today for an item (0 for deferred fee options). */
  const getTodayCharge = (item?: AddOnDataItem): number => {
    if (!item) return 0;
    if (
      item.addon_performance_account_fee_option === AddonPerformanceAccountFee.MONTHLY ||
      item.addon_performance_account_fee_option === AddonPerformanceAccountFee.LIFETIME_ONE_TIME
    ) {
      return 0;
    }
    return item.price ?? 0;
  };

  const total = useMemo(() => {
    let _total = 0;
    addOnsData.forEach((group) => {
      const sel = addOns[group.addon_type];
      if (!sel) return;
      const processUid = (uid: string) => {
        const item = findItemByUid(group, uid);
        _total += getTodayCharge(item);
      };
      if (Array.isArray(sel)) sel.forEach(processUid);
      else processUid(sel);
    });
    return _total;
  }, [addOns, addOnsData]);

  const accountPrice = useMemo(() => {
    if (sizeIndex === undefined || !packages) return 0;

    return packages?.[sizeIndex ?? 0]?.billing_plan?.first_price ?? 0;
  }, [sizeIndex, packages]);

  const selectedPackage = useMemo(() => {
    return packages?.[sizeIndex ?? 0];
  }, [packages, sizeIndex]);

  const isProductSubscription = useMemo(() => {
    const rebillType = (selectedPackage?.billing_plan as { rebill_times_type?: string } | undefined)
      ?.rebill_times_type;
    return SUBSCRIPTION_REBILL_TYPES.has(rebillType || '');
  }, [selectedPackage]);

  const isMonthlyPaymentHandling = useMemo(() => {
    return addOns['default'] === 'default__s_0';
  }, [addOns]);

  const paymentHandlingMonthlyPrice = useMemo(() => {
    if (!isMonthlyPaymentHandling) return 0;

    const defaultGroup = addOnsData.find((g) => g.addon_type === 'default');
    const selectedDefault = addOns['default'];
    if (!defaultGroup || !selectedDefault || Array.isArray(selectedDefault))
      return 0;

    const selectedItem = findItemByUid(defaultGroup, selectedDefault);
    return selectedItem?.price ?? 0;
  }, [addOns, addOnsData, isMonthlyPaymentHandling]);

  const addOnsMonthlyPrice = useMemo(() => {
    if (!isMonthlyPaymentHandling) return 0;

    let monthlyTotal = 0;

    addOnsData.forEach((group) => {
      if (group.addon_type === 'default') return;
      if (MONTHLY_EXCLUDED_ADDON_TYPES.has(group.addon_type)) return;

      const selected = addOns[group.addon_type];
      if (!selected) return;

      const processUid = (uid: string) => {
        const item = findItemByUid(group, uid);
        if (!item) return;

        monthlyTotal += item.addonMonthlyPrice ?? 0;
      };

      if (Array.isArray(selected)) selected.forEach(processUid);
      else processUid(selected);
    });

    return monthlyTotal;
  }, [addOns, addOnsData, isMonthlyPaymentHandling]);

  const productSecondPrice = useMemo(() => {
    if (!isProductSubscription) return 0;

    return selectedPackage?.billing_plan?.second_price ?? 0;
  }, [isProductSubscription, selectedPackage]);

  const totalDueTodayPrice = useMemo(() => {
    if (platform === TradingPlatForms.RITHMIC_DATA_FEED && isCompetitionAccount)
      return accountPrice + 35 + total;

    if (platform === TradingPlatForms.RITHMIC_DATA_FEED) return accountPrice;

    return accountPrice + 20 + total;
  }, [accountPrice, platform, isCompetitionAccount, total]);

  const monthlyPrice = useMemo(() => {
    return (
      productSecondPrice +
      (isMonthlyPaymentHandling
        ? addOnsMonthlyPrice + paymentHandlingMonthlyPrice
        : 0)
    );
  }, [
    addOnsMonthlyPrice,
    isMonthlyPaymentHandling,
    paymentHandlingMonthlyPrice,
    productSecondPrice,
  ]);

  const handleContinue = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setPayment({ terms_accepted: [] });
    if (!isPurchaseMode) {
      setShowTerm(true);
    } else {
      navigate('/review-checkout');
    }
  };

  const priceStr = useMemo(() => {
    return '$15.00';
  }, [isCompetitionAccount]);

  if (!platform) return null;

  if (showTerm)
    return (
      <TermAndCondition
        onBack={() => {
          setShowTerm(false);
          setPayment({ terms_accepted: [] });
        }}
        onConfirm={() => {
          navigate('/review-checkout');
        }}
      />
    );

  return (
    <>
      <div className="min-h-screen pb-[120px] pt-4 ">
        <div className="max-w-[1440px] m-auto px-6 pb-10 lg:px-[60px] pt-4 flex flex-row justify-between gap-x-10 bg-[#FFFFFF0D] backdrop-blur-[200px]">
          <div className="flex-1">
            <div className="mb-8">
              <StepProgress
                isAddons
                accountType={typeIndex}
                current={isPurchaseMode ? 3 : 4}
                platform={platform}
                selectedPackage={packages?.[0]}
                skipPlatform={isPurchaseMode}
                total={isPurchaseMode ? 4 : 5}
                onClick={(step) => {
                  if (!isPurchaseMode && !step)
                    navigate('/select-trading-platform');
                  if (step === (isPurchaseMode ? 0 : 1))
                    navigate('/select-trading-account-type');
                  if (step === (isPurchaseMode ? 1 : 2))
                    navigate('/select-account');
                }}
              />
            </div>
            <div className="flex flex-col-reverse lg:flex-row gap-[22px]">
              <div className="flex-1">
                <h2 className="font-bold text-[28px] leading-[100%] tracking-normal text-white mb-2">
                  Select Add-Ons
                </h2>
                <p className="pt-[10px] font-medium text-[16px] leading-[100%] tracking-normal text-white mb-[36px]">
                  Customize your trading account with additional features
                </p>
                <div className="flex flex-col gap-[40px]">
                  {addOnsData.map((addOn, index) => (
                    <div
                      key={index}
                      className={`bg-[#25334880] w-full rounded-[10px] border border-[#33455F] pt-[20px] pb-[20px] pl-[30px] pr-[30px] ${!addOn?.addon_type || availableAddOnsTypes[typeIndex || 1].includes(addOn?.addon_type || '') ? '' : 'opacity-50 pointer-events-none'}`}
                    >
                      <div className="flex flex-col gap-[10px]">
                        <div className="flex items-center justify-between gap-[20px] text-white">
                          <div>
                            <span className="text-[18px] font-bold">
                              {addOn?.title}
                            </span>
                            <div className="text-[#CECECE] font-normal text-[12px] leading-[100%]">
                              {addOn.description}
                            </div>
                          </div>
                          {!addOn.optional ? (
                            <span className="text-[14px]">Select 1</span>
                          ) : (
                            <span className="text-[14px]">Optional</span>
                          )}
                        </div>
                        {addOn.data.map((d, idx) => (
                          <div
                            key={`${idx}_${index}`}
                            className={`bg-[#171D23] p-[20px] rounded-[10px] border border-[#33455F] flex justify-between items-center cursor-pointer min-h-[80px]`}
                            onClick={() => selectAddOns(addOn.addon_type, d.uid)}
                          >
                            <div className="flex-1 max-w-[306px]">
                              <div className="text-[14px] font-bold">
                                {d?.title}
                              </div>
                              <div className="text-[#CECECE] font-normal text-[12px]">
                                {addOn.addon_type === 'pa_change_30pct_rule' ||
                                addOn.addon_type === 'eval_quality_x_days'
                                  ? ''
                                  : d.addon_performance_account_fee_option ===
                                      AddonPerformanceAccountFee.MONTHLY
                                    ? `$${d.price}/ month (Pay when you qualify and $88 by default)`
                                    : d.addon_performance_account_fee_option ===
                                        AddonPerformanceAccountFee.LIFETIME_ONE_TIME
                                      ? `$${d.price} (Pay when you qualify)`
                                      : d.addon_performance_account_fee_option ===
                                          AddonPerformanceAccountFee.LIFETIME_PAY_PARTIAL
                                        ? `Pay $${d.price} today, then $${d?.priceLater} only if you qualify when activating your Performance Account.`
                                        : addOn.addon_type ===
                                              AddonType.EvalDiscountXPercentReset &&
                                            d.discount_percent
                                          ? `${d.discount_percent}% if buying it now`
                                          : d.sub}
                              </div>
                            </div>
                            <div
                              className={`text-start ${
                                (isMonthlyPaymentHandling &&
                                  !MONTHLY_EXCLUDED_ADDON_TYPES.has(addOn.addon_type) &&
                                  (d.addonMonthlyPrice ?? 0) < 0) ||
                                (!isMonthlyPaymentHandling && (d.price ?? 0) < 0)
                                  ? 'text-[#C83837]'
                                  : 'text-[#007AFE]'
                              } relative min-w-[20%]`}
                            >
                              {d.addon_performance_account_fee_option ===
                                AddonPerformanceAccountFee.MONTHLY ||
                              d.addon_performance_account_fee_option ===
                                AddonPerformanceAccountFee.LIFETIME_ONE_TIME ? (
                                <span className="font-semibold text-[14px] leading-[100%] tracking-normal">
                                  $0 Today
                                </span>
                              ) : addOn.addon_type === 'default' &&
                                d.uid === 'default__s_0' ? (
                                <span className="font-semibold text-[14px] leading-[100%] tracking-normal">
                                  $5.00/month
                                </span>
                              ) : isMonthlyPaymentHandling &&
                                !MONTHLY_EXCLUDED_ADDON_TYPES.has(addOn.addon_type) ? (
                                <span className="font-semibold text-[14px] leading-[100%] tracking-normal">
                                  {(d.addonMonthlyPrice ?? 0) === 0
                                    ? '$0'
                                    : `${(d.addonMonthlyPrice ?? 0) >= 0 ? '+' : '-'}$${Math.abs(
                                        d.addonMonthlyPrice ?? 0
                                      ).toFixed(2)}/month`}
                                </span>
                              ) : (
                                <span className="font-semibold text-[14px] leading-[100%] tracking-normal">
                                  {(d.price ?? 0) === 0
                                    ? '$0'
                                    : `${(d.price ?? 0) >= 0 ? '+' : '-'}$${Math.abs(
                                        d.price ?? 0
                                      ).toFixed(2)}${
                                        d.first_period_unit?.toLocaleLowerCase() ===
                                        'month'
                                          ? '/month'
                                          : d.first_period_unit?.toLocaleLowerCase() ===
                                                'today' ||
                                              d.addon_performance_account_fee_option
                                            ? ' Today'
                                            : ''
                                      }`}
                                </span>
                              )}
                              <div className="text-white font-normal text-[12px] leading-[100%]">
                                {d.priceNote}
                              </div>
                              {d.rangular ? (
                                <svg
                                  className="absolute top-[-23px] left-1/2 -translate-x-1/2"
                                  fill="none"
                                  height="23"
                                  viewBox="0 0 49 23"
                                  width="49"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <line
                                    stroke="#C83837"
                                    x1="1.21197"
                                    x2="48.212"
                                    y1="0.547155"
                                    y2="22.5472"
                                  />
                                  <line
                                    stroke="#C83837"
                                    x1="0.797962"
                                    x2="48.2669"
                                    y1="21.5118"
                                    y2="0.542634"
                                  />
                                </svg>
                              ) : null}
                            </div>
                            <div
                              className={`${
                                isItemSelected(addOn.addon_type, d.uid)
                                  ? 'bg-[#007AFE]'
                                  : 'bg-[#2A394C]'
                              } h-5 w-5 rounded-[20px] flex items-center justify-center`}
                            >
                              {isItemSelected(addOn.addon_type, d.uid) && (
                                <svg
                                  className="h-4 w-4 text-white"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    d="M5 13l4 4L19 7"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                  />
                                </svg>
                              )}
                            </div>
                          </div>
                        ))}
                        {addOn.title2 && (
                          <div className="flex items-center justify-between gap-[20px] text-white mt-4">
                            <span className="text-[18px] font-bold">
                              {addOn.title2}
                            </span>
                          </div>
                        )}
                        {addOn.data2?.map((d, idx) => (
                          <div
                            key={`data2_${idx}_${index}`}
                            className={`bg-[#171D23] p-[20px] rounded-[10px] border border-[#33455F] flex justify-between items-center cursor-pointer `}
                            onClick={() => selectAddOns(addOn.addon_type, d.uid)}
                          >
                            <div
                              className={` ${(d.price ?? 0) < 0 ? 'text-[#C83837]' : 'text-[#007AFE]'} relative`}
                            >
                              <span className="font-semibold text-[14px] leading-[100%] tracking-normal">
                                {d.price !== 0 ? (
                                  <>
                                    {(d.price ?? 0) >= 0 ? '+' : '-'}${d.title}{' '}
                                    - $
                                    {Math.abs(
                                      isMonthlyPaymentHandling &&
                                        !MONTHLY_EXCLUDED_ADDON_TYPES.has(addOn.addon_type)
                                        ? (d.addonMonthlyPrice ?? 0)
                                        : (d.price ?? 0)
                                    ).toFixed(2)}
                                    {isMonthlyPaymentHandling &&
                                    !MONTHLY_EXCLUDED_ADDON_TYPES.has(addOn.addon_type)
                                      ? '/month'
                                      : ''}
                                  </>
                                ) : (
                                  <>$0 part of base price</>
                                )}
                              </span>
                            </div>
                            <div
                              className={`${
                                isItemSelected(addOn.addon_type, d.uid)
                                  ? 'bg-[#007AFE]'
                                  : 'bg-[#2A394C]'
                              } h-5 w-5 rounded-[20px] flex items-center justify-center`}
                            >
                              {isItemSelected(addOn.addon_type, d.uid) && (
                                <svg
                                  className="h-4 w-4 text-white"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    d="M5 13l4 4L19 7"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                  />
                                </svg>
                              )}
                            </div>
                          </div>
                        ))}
                        {addOn.note && (
                          <div
                            dangerouslySetInnerHTML={{ __html: addOn.note }}
                            className="text-xs text-white"
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="relative lg:w-[340px] base:w-full bg-[#1F2734] rounded-[20px] pt-[30px] pb-[30px] pl-[24px] pr-[24px] flex flex-col gap-[24px]">
                <div className="sticky top-[30px]">
                  <div className="bg-[#253348] pt-[30px] pb-[30px] pl-[24px] pr-[24px] border border-[#33455F] rounded-[10px] gap-[30px] flex flex-col">
                    <span className="font-bold text-[16px] leading-[100%] tracking-normal text-white">
                      Your Selection
                    </span>
                    <div>
                      <div className="flex items-center gap-[23px] h-[46px]">
                        <svg
                          fill="none"
                          height="30"
                          viewBox="0 0 40 40"
                          width="30"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M17.6033 26.5133L28.2633 15.8533L27.0833 14.6733L17.6033 24.1533L12.8533 19.4033L11.6733 20.5833L17.6033 26.5133ZM20.005 35C17.9317 35 15.9817 34.6067 14.155 33.82C12.3294 33.0322 10.7411 31.9633 9.39 30.6133C8.03889 29.2633 6.96944 27.6767 6.18167 25.8533C5.39389 24.03 5 22.0806 5 20.005C5 17.9294 5.39389 15.9794 6.18167 14.155C6.96833 12.3294 8.03556 10.7411 9.38333 9.39C10.7311 8.03889 12.3183 6.96944 14.145 6.18167C15.9717 5.39389 17.9217 5 19.995 5C22.0683 5 24.0183 5.39389 25.845 6.18167C27.6706 6.96833 29.2589 8.03611 30.61 9.385C31.9611 10.7339 33.0306 12.3211 33.8183 14.1467C34.6061 15.9722 35 17.9217 35 19.995C35 22.0683 34.6067 24.0183 33.82 25.845C33.0333 27.6717 31.9644 29.26 30.6133 30.61C29.2622 31.96 27.6756 33.0294 25.8533 33.8183C24.0311 34.6072 22.0817 35.0011 20.005 35Z"
                            fill="#14CA74"
                          />
                        </svg>
                        <div>
                          <div className="text-[12px] text-[#CECECE]">
                            Trading Platform
                          </div>
                          <div className="flex items-center gap-[10px]  mt-[5px]">
                            {
                              iconsImgPackage[
                                platform as keyof typeof iconsImgPackage
                              ].icon
                            }
                            <span className="font-semibold text-[14px] text-white">
                              {
                                iconsImgPackage[
                                  platform as keyof typeof iconsImgPackage
                                ].text
                              }
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="w-[30px] flex justify-center ">
                        <div className="bg-[#33455F] w-[4px] h-[26px] rounded" />
                      </div>
                      <div className="flex items-center gap-[23px] h-[46px]">
                        <svg
                          fill="none"
                          height="30"
                          viewBox="0 0 40 40"
                          width="30"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M17.6033 26.5133L28.2633 15.8533L27.0833 14.6733L17.6033 24.1533L12.8533 19.4033L11.6733 20.5833L17.6033 26.5133ZM20.005 35C17.9317 35 15.9817 34.6067 14.155 33.82C12.3294 33.0322 10.7411 31.9633 9.39 30.6133C8.03889 29.2633 6.96944 27.6767 6.18167 25.8533C5.39389 24.03 5 22.0806 5 20.005C5 17.9294 5.39389 15.9794 6.18167 14.155C6.96833 12.3294 8.03556 10.7411 9.38333 9.39C10.7311 8.03889 12.3183 6.96944 14.145 6.18167C15.9717 5.39389 17.9217 5 19.995 5C22.0683 5 24.0183 5.39389 25.845 6.18167C27.6706 6.96833 29.2589 8.03611 30.61 9.385C31.9611 10.7339 33.0306 12.3211 33.8183 14.1467C34.6061 15.9722 35 17.9217 35 19.995C35 22.0683 34.6067 24.0183 33.82 25.845C33.0333 27.6717 31.9644 29.26 30.6133 30.61C29.2622 31.96 27.6756 33.0294 25.8533 33.8183C24.0311 34.6072 22.0817 35.0011 20.005 35Z"
                            fill="#14CA74"
                          />
                        </svg>
                        <div>
                          <div className="text-[12px] text-[#CECECE]">
                            Account Type
                          </div>
                          <div className="font-semibold text-[14px]">
                            {accountTypeData[(typeIndex || 1) - 1]?.title}
                          </div>
                        </div>
                      </div>
                      <div className="w-[30px] flex justify-center ">
                        <div className="bg-[#33455F] w-[4px] h-[26px] rounded" />
                      </div>
                      <div className="flex items-center gap-[23px] h-[46px]">
                        <svg
                          fill="none"
                          height="30"
                          viewBox="0 0 40 40"
                          width="30"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M17.6033 26.5133L28.2633 15.8533L27.0833 14.6733L17.6033 24.1533L12.8533 19.4033L11.6733 20.5833L17.6033 26.5133ZM20.005 35C17.9317 35 15.9817 34.6067 14.155 33.82C12.3294 33.0322 10.7411 31.9633 9.39 30.6133C8.03889 29.2633 6.96944 27.6767 6.18167 25.8533C5.39389 24.03 5 22.0806 5 20.005C5 17.9294 5.39389 15.9794 6.18167 14.155C6.96833 12.3294 8.03556 10.7411 9.38333 9.39C10.7311 8.03889 12.3183 6.96944 14.145 6.18167C15.9717 5.39389 17.9217 5 19.995 5C22.0683 5 24.0183 5.39389 25.845 6.18167C27.6706 6.96833 29.2589 8.03611 30.61 9.385C31.9611 10.7339 33.0306 12.3211 33.8183 14.1467C34.6061 15.9722 35 17.9217 35 19.995C35 22.0683 34.6067 24.0183 33.82 25.845C33.0333 27.6717 31.9644 29.26 30.6133 30.61C29.2622 31.96 27.6756 33.0294 25.8533 33.8183C24.0311 34.6072 22.0817 35.0011 20.005 35Z"
                            fill="#14CA74"
                          />
                        </svg>
                        <div>
                          <div className="text-[12px] text-[#CECECE]">
                            Account Size
                          </div>
                          <div className="font-semibold text-[14px] break-words whitespace-pre-line max-w-[180px] truncate">
                            {packages?.[0] !== undefined ? (
                              packages?.[0]?.display_name_app + ' Account'
                            ) : (
                              <span className="text-[14px] font-normal">
                                Select Base Account
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="w-[30px] flex justify-center ">
                        <div className="bg-[#33455F] w-[4px] h-[26px] rounded" />
                      </div>
                      <div className="flex items-center gap-[23px] h-[46px]">
                        <svg
                          fill="none"
                          height="30"
                          viewBox="0 0 40 40"
                          width="30"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M17.6033 26.5133L28.2633 15.8533L27.0833 14.6733L17.6033 24.1533L12.8533 19.4033L11.6733 20.5833L17.6033 26.5133ZM20.005 35C17.9317 35 15.9817 34.6067 14.155 33.82C12.3294 33.0322 10.7411 31.9633 9.39 30.6133C8.03889 29.2633 6.96944 27.6767 6.18167 25.8533C5.39389 24.03 5 22.0806 5 20.005C5 17.9294 5.39389 15.9794 6.18167 14.155C6.96833 12.3294 8.03556 10.7411 9.38333 9.39C10.7311 8.03889 12.3183 6.96944 14.145 6.18167C15.9717 5.39389 17.9217 5 19.995 5C22.0683 5 24.0183 5.39389 25.845 6.18167C27.6706 6.96833 29.2589 8.03611 30.61 9.385C31.9611 10.7339 33.0306 12.3211 33.8183 14.1467C34.6061 15.9722 35 17.9217 35 19.995C35 22.0683 34.6067 24.0183 33.82 25.845C33.0333 27.6717 31.9644 29.26 30.6133 30.61C29.2622 31.96 27.6756 33.0294 25.8533 33.8183C24.0311 34.6072 22.0817 35.0011 20.005 35Z"
                            fill="#33455F"
                          />
                        </svg>
                        <div>
                          <div className="text-[12px] text-[#CECECE]">
                            Add Ons
                          </div>
                          <div className="font-semibold text-[14px]">
                            See details on this page
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col gap-[20px]">
                        <div className="mt-[30px] h-[3px] bg-[#33455F] w-full" />
                        <div className="flex flex-col gap-[10px]">
                          <div className="flex items-center gap-[23px] justify-between leading-[17px]">
                            <div className="text-[14px] font-medium text-white">
                              {
                                iconsImgPackage[
                                  platform as keyof typeof iconsImgPackage
                                ].text
                              }
                            </div>
                            <div className="font-medium text-[14px]">
                              $20.00
                            </div>
                          </div>
                          <div className="flex items-center gap-[23px] justify-between leading-[17px]">
                            <div className="text-[14px] font-medium text-white">
                              {sizeIndex === undefined
                                ? 'Choose account'
                                : `${packages?.[sizeIndex ?? 0]?.display_name_app} Account (Monthly)`}
                            </div>
                            <div className="font-medium text-[14px]">
                              ${accountPrice.toFixed(2)}
                            </div>
                          </div>
                        </div>
                        {platform === TradingPlatForms.RITHMIC_DATA_FEED &&
                          isCompetitionAccount && (
                            <div className="flex items-center gap-[23px] justify-between leading-[17px]">
                              <div className="text-[14px] font-medium text-white">
                                Competition Account
                              </div>
                              <div className="font-medium text-[14px]">
                                $15.00
                              </div>
                            </div>
                          )}
                        <div className="h-[3px] bg-[#33455F] w-full" />

                        <div className="flex items-center gap-[23px] justify-between h-[17px]">
                          <div className="text-[14px] font-bold text-white">
                            Total Due Today
                          </div>
                          <div className="font-bold text-[14px]">
                            ${totalDueTodayPrice.toFixed(2)}
                          </div>
                        </div>
                        <div className="flex items-center gap-[23px] justify-between h-[17px]">
                          <div className="text-[14px] font-bold text-white">
                            Total (Monthly)
                          </div>
                          <div className="font-bold text-[14px]">
                            ${monthlyPrice.toFixed(2)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  {platform === TradingPlatForms.RITHMIC_DATA_FEED ? (
                    <div className="mt-3">
                      <span className="text-[18px] font-bold">
                        Only in Rithmic
                      </span>
                      <div className="mt-[10px]">
                        <div className="border border-[#C83837] rounded-[10px] bg-[#253348] pt-[20px] pb-[20px] pl-[18px] pr-[18px] flex flex-col gap-[20px]">
                          <div className="flex items-center justify-between">
                            <span>Competition Account</span>
                          </div>
                          <div className="flex flex-col gap-[12px]">
                            <div className="flex items-center gap-[10px] font-medium">
                              {iconsPackage[0]} Unlimited trades per day
                            </div>
                            <div className="flex items-center gap-[10px] font-medium">
                              {iconsPackage[2]} Advanced risk algorithms
                            </div>
                            <div className="flex items-center gap-[10px] font-medium">
                              {iconsPackage[3]} 24/7 dedicated support
                            </div>
                          </div>
                          <div className="h-[3px] bg-[#33455F] w-full" />
                          <div className="flex items-center gap-[10px] font-medium justify-between">
                            <span>One-Time</span>
                            <span className="font-bold text-[20px]">
                              {priceStr}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full sticky bottom-0 backdrop-blur-[30px] bg-[linear-gradient(90deg,rgba(23,29,35,0.8)_0%,rgba(23,29,35,0.8)_100%)] border-t border-[#255589] h-[100px]">
        <div className="max-w-[1440px] m-auto px-6 lg:px-[60px] flex justify-between items-center h-full">
          <Button className="px-8" size="lg" variant="outline" onClick={goBack}>
            Previous: Select Account Size
          </Button>
          <Button
            className="px-8"
            size="lg"
            variant="gradient"
            onClick={handleContinue}
          >
            Continue to Checkout
          </Button>
        </div>
      </div>
    </>
  );
};
